import { MenuItem } from "@mui/material";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import timeIcon from "./assets/time.svg";

import { useState } from "react";

import styles from "./FilterView.module.scss";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";

import utc from "dayjs/plugin/utc"; 
import { AggregateWindow } from "../../../types/types";
import Modal from "../../../../../common/components/molecule/Modal/Modal";
import { converteTitle } from "../../../../../common/utils/graph.utils";
import Button from "../../../../../common/components/atom/ButtonV2/Button";

dayjs.extend(utc);

export type Preset = "today" | "this-month" | "this-year";

export interface FilterViewProps {
  aggregateWindow?: AggregateWindow | null;
  setAggregateWindow: (aggregateWindow: AggregateWindow) => void;
  devEuiValue?: string;
  setDevEuiValue?: (devEuiValue: string) => void;
  onSubmit?: () => void;
  isLoading?: boolean;
  handleNextWindow?: () => void;
  handlePreviousWindow?: () => void;
  setDateStart?: (date: Date | null) => void;
  setDateStop?: (date: Date | null) => void;
  dateStart?: Date;
  dateStop?: Date;
  onClickPreset?: (Presets: Preset) => void;
  disabledSubmit?: boolean;
  disabledAggregation?: AggregateWindow[];
  presetWindowActive?: Preset;
}
export default function FilterView({
  aggregateWindow,
  setAggregateWindow,
  setDevEuiValue,
  devEuiValue,
  onSubmit,
  isLoading,
  handleNextWindow,
  handlePreviousWindow,
  setDateStart,
  setDateStop,
  dateStart,
  dateStop,
  onClickPreset,
  disabledSubmit,
  disabledAggregation,
  presetWindowActive

}: FilterViewProps) {
  const handleChange = (event: SelectChangeEvent) => {
    setAggregateWindow(event.target.value as AggregateWindow);
  };

  const [open, setOpen] = useState(false);
  const [showPrevTooltip, setShowPrevTooltip] = useState(false);
  const [showNextTooltip, setShowNextTooltip] = useState(false);

  const getTooltipText = (isNext: boolean) => {
    switch (aggregateWindow) {
      case "1h":
        return isNext ? "Jour suivant" : "Jour précédent";
      case "15m":
        return isNext ? "4 heures suivantes" : "4 heures précédentes";
      case "1d":
        return isNext ? "Mois suivant" : "Mois précédent";
      case "1mo":
        return isNext ? "Année suivante" : "Année précédente";
      default:
        return isNext ? "Suivant" : "Précédent";
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleDateStartChange = (date: Dayjs | null) => {
    const dateValue = date?.utc().toDate() || null;

    setDateStart && setDateStart(dateValue);
  
  };

  const handleDateStopChange = (date: Dayjs | null) => {
    const dateValue = date?.utc().toDate() || null;

    setDateStop && setDateStop(dateValue);
   
  };

  const handleOnClickPreset = (preset: Preset) => {
      onClickPreset && onClickPreset(preset);
      handleClose();
  }



  const checkDisabledAggregation = (value: AggregateWindow) => {
    return disabledAggregation?.includes(value);
  }


  return (
    <>
      <div className={styles["filter"]}>
        {handlePreviousWindow && (
          <div 
            className={styles["nav-button-wrapper"]}
            onMouseEnter={() => setShowPrevTooltip(true)}
            onMouseLeave={() => setShowPrevTooltip(false)}
          >
            <button
              onClick={handlePreviousWindow}
              className={styles["nav-button"]}
              aria-label="Fenêtre précédente"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {showPrevTooltip && (
              <div className={styles["tooltip"]}>
                {getTooltipText(false)}
              </div>
            )}
          </div>
        )}

        <Button
          onClick={handleOpen}
          variant="contained"
  
          style={{ height: 40 }}
          iconRightType="arrow-down"
          color= "white"
          LeftIconRender={() => <img className={styles['icon-time']} src={timeIcon} alt="time" />}
        >
          {converteTitle(dateStart?.toISOString() || "", dateStop?.toISOString() || "")}
        </Button>

        {handleNextWindow && (
          <div 
            className={styles["nav-button-wrapper"]}
            onMouseEnter={() => setShowNextTooltip(true)}
            onMouseLeave={() => setShowNextTooltip(false)}
          >
            <button
              onClick={handleNextWindow}
              className={styles["nav-button"]}
              aria-label="Fenêtre suivante"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {showNextTooltip && (
              <div className={styles["tooltip"]}>
                {getTooltipText(true)}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal handleClose={handleClose} isOpen={open}>
        <div>
          <div className={styles["modal-content"]}>
            <div
              className={[styles["container"], styles["container--left"]].join(
                " "
              )}
            >
              <h2 className={styles["container__title"]}>Aggrégation</h2>
              <Select
                labelId="demo-simple-select-standard-label"
                id="demo-simple-select-standard"
                value={aggregateWindow || ""}
                onChange={handleChange}
              >
                <MenuItem value={"15m"} disabled={checkDisabledAggregation('15m')}>Par 15 minutes</MenuItem>
                <MenuItem value={"1h"} disabled={checkDisabledAggregation('1h')}>Par heure</MenuItem>
                <MenuItem value={"1d"}  disabled={checkDisabledAggregation('1d')}>Par jour</MenuItem>
                <MenuItem value={"1mo"}  disabled={checkDisabledAggregation('1mo')}>Par mois</MenuItem>
              </Select>
            </div>
            <div className={styles["separator"]} />
            <div
              className={[styles["container"], styles["container--right"]].join(
                " "
              )}
            >
              <h2 className={styles["container__title"]}>Intervalle</h2>

              {onClickPreset && (
                <div className={styles["preset_container"]}>
                  <h3 className={styles["container__sub-title"]}>
                    Invervalles prédéfinis
                  </h3>
                  <div className={[styles["preset_container__btn"]].join(" ")}>
                    <Button
                      onClick={() => { handleOnClickPreset("today"); }}
                      variant={ presetWindowActive === "today" ? "contained" : "bordered"}
                      color="primary"
                      style={{ height: 40 }}
                    >
                      Aujourd'hui
                    </Button>
                  </div>
                  <div className={[styles["preset_container__btn"]].join(" ")}>
                    <Button
                      onClick={() => { handleOnClickPreset("this-month")}}
                      variant={ presetWindowActive === "this-month" ? "contained" : "bordered"}
                      color="primary"
                      style={{ height: 40 }}
                    >
                      Ce mois-ci
                    </Button>
                  </div>
                  <div className={[styles["preset_container__btn"]].join(" ")}>
                    <Button
                      onClick={() => { handleOnClickPreset("this-year")}}
                      variant={ presetWindowActive === "this-year" ? "contained" : "bordered"}
                      color="primary"
                      style={{ height: 40 }}
                    >
                      Cette année
                    </Button>
                  </div>
                </div>
              )}
              <div className={styles["custom-interval-container"]}>
                <h3 className={styles["container__sub-title"]}>
                  Invervalle personalisé
                </h3>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <div
                    className={[
                      styles["custom-interval-container__input"],
                      styles["custom-interval-container__input--date-start"],
                    ].join(" ")}
                  >
                    <DatePicker
                      label="Date de début"
                      onChange={handleDateStartChange}
                      value={dateStart && dayjs(dateStart)}
                      format="DD/MM/YYYY"
                      timezone="UTC"
                    />
                  </div>
                  <div
                    className={[
                      styles["custom-interval-container__input"],
                      styles["custom-interval-container__input--date-end"],
                    ].join(" ")}
                  >
                    <DatePicker
                      label="Date de fin"
                      onChange={handleDateStopChange}
                      value={dateStop && dayjs(dateStop)}
                      minDate={dateStart && dayjs(dateStart)}
                      format="DD/MM/YYYY"
                      timezone="UTC"
                    />
                  </div>
                </LocalizationProvider>
              </div>
            </div>
          </div>
          <div className={styles["btn-container"]}>
            <Button
              onClick={() => {
                onSubmit && onSubmit();
                handleClose();
              }}
              color="primary"
              variant="contained"
              style={{ height: 40 }}
              disabled={disabledSubmit}
            >
              Appliquer
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
